import { useLocation, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { IReviewerDeclineAction } from '@publishing-engine/domain/types';
import { getReviewRequestContextFromRoute } from 'src/contexts/ReviewDataProvider';
import {
  useCoReviewDecline,
  useReviewDecline,
  useReviewInvitationDecline
} from 'src/api/commands';
import { useToast, Button } from '@morressier/alembic';
import { useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { BrandLogo } from '../SignUpInviteSent';
import axiosInstance from '../../api/client';

type TokenData = {
  userId: string;
  action: string;
  entityId: string;
  submissionId: string;
  submissionContextId: string;
  organizationId: string;
};

export const DeclineReviews = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const searchParams = new URLSearchParams(location.search);
  const action = searchParams.get('action') as IReviewerDeclineAction;
  const userId = searchParams.get('userId');
  const token = searchParams.get('token') || '';

  const [isProcessing, setIsProcessing] = useState(false);
  const [lookupError, setLookupError] = useState(false);

  const { reviewId } = getReviewRequestContextFromRoute();
  const queryKey = ['review', { id: reviewId }];

  const declineCoReviewMutation = useCoReviewDecline({
    reviewId: reviewId,
    userId: userId as string,
    queryKey
  });

  if (!action) {
    navigate(`/app/reviews/${reviewId}`, { replace: true });
    return <></>;
  }

  const reviewDeclineMutation = useReviewDecline({
    reviewId: reviewId,
    userId: userId as string,
    queryKey,
    token
  });

  const reviewInvitationDeclineMutation = useReviewInvitationDecline({
    reviewId: reviewId,
    userId: userId as string,
    queryKey
  });

  const declineReviewAndRedirect = async (
    submissionId: string,
    submissionContextId: string,
    commandType: string
  ) => {
    setIsProcessing(true);
    try {
      const response = token
        ? await reviewDeclineMutation.mutateAsync(commandType)
        : await reviewInvitationDeclineMutation.mutateAsync();

      if (response) {
        navigate(
          `/reviews/${reviewId}/decline-review?submissionId=${submissionId}&ctxId=${submissionContextId}`,
          { replace: true }
        );
      }
    } catch (err) {
      if (isAxiosError(err) && err.status === 401) {
        toast({
          variant: 'error',
          title: err.response?.statusText ?? 'Command Failed',
          description: 'You do not have permissions to decline the review',
          isClosable: true
        });
      } else if (isAxiosError(err) && err.status === 400) {
        toast({
          variant: 'attention',
          title: err.response?.data?.message ?? err.message,
          isClosable: true
        });
        navigate('/app/home', { replace: true });
      } else if (isAxiosError(err) && err.status === 500) {
        toast({
          variant: 'attention',
          title:
            'Your review decision has already been processed and has moved to the next stage',
          isClosable: true
        });
        navigate('/app/home', { replace: true });
      } else {
        toast({
          variant: 'error',
          title: 'Unexpected Error',
          description:
            (isAxiosError(err) && err.response?.data?.message) ??
            'Something went wrong...',
          isClosable: true
        });
      }

      navigate('/app/home', { replace: true });
    } finally {
      setIsProcessing(false);
    }
  };

  const declineCoReviewAndRedirect = async (
    submissionId: string,
    submissionContextId: string
  ) => {
    setIsProcessing(true);
    try {
      const response = await declineCoReviewMutation.mutateAsync();

      if (response) {
        navigate(
          `/reviews/${reviewId}/decline-coreview?submissionId=${submissionId}&ctxId=${submissionContextId}`,
          { replace: true }
        );
      }
    } catch (err) {
      if (isAxiosError(err) && err.status === 401) {
        toast({
          variant: 'error',
          title: err.response?.statusText ?? 'Command Failed',
          description: 'You do not have permissions to decline the review',
          isClosable: true
        });
      } else if (isAxiosError(err) && err.status === 400) {
        toast({
          variant: 'attention',
          title: err.response?.data?.message ?? err.message,
          isClosable: true
        });
        navigate('/app/home', { replace: true });
      } else if (isAxiosError(err) && err.status === 500) {
        toast({
          variant: 'attention',
          title:
            'Your review decision has already been processed and has moved to the next stage',
          isClosable: true
        });
        navigate('/app/home', { replace: true });
      } else {
        toast({
          variant: 'error',
          title: 'Unexpected Error',
          description:
            (isAxiosError(err) && err.response?.data?.message) ??
            'Something went wrong...',
          isClosable: true
        });
      }
      navigate('/app/home', { replace: true });
    } finally {
      setIsProcessing(false);
    }
  };

  // Centralized click handler
  const handleDeclineClick = async () => {
    let submissionId = '';
    let submissionContextId = '';
    let commandType = '';

    if (token) {
      try {
        const res = await axiosInstance.get<TokenData>(`/email/token/${token}`);
        submissionId = res.data.submissionId;
        submissionContextId = res.data.submissionContextId;
        commandType = res.data.action;
      } catch (err) {
        setLookupError(true);
        if (isAxiosError(err) && err.status === 500) {
          toast({
            variant: 'attention',
            title:
              'Your review decision has already been processed and has moved to the next stage',
            isClosable: true
          });
          navigate('/app/home', { replace: true });
        } else {
          toast({
            variant: 'error',
            title: 'Unexpected Error',
            description:
              (isAxiosError(err) && err.response?.statusText) ??
              'Something went wrong...',
            isClosable: true
          });
        }

        // Do not submit a decline request when the token lookup fails.
        return;
      }
    }

    if (action === 'declineFromEmail') {
      declineReviewAndRedirect(submissionId, submissionContextId, commandType);
    } else if (action === 'declineCoReviewFromEmail') {
      declineCoReviewAndRedirect(submissionId, submissionContextId);
    }
  };

  if (lookupError) return <Text>Error: invalid or expired token</Text>;

  return (
    <Flex direction="column" minH="100vh" w="full">
      <Flex
        py={2}
        px={4}
        justify="space-between"
        align="center"
        boxShadow="elevation3"
        position="sticky"
        top={0}
        zIndex={1000}
        bgColor="surfacePrimaryFill"
        flexShrink={0}
      >
        <Flex align="center" gap={2}>
          <BrandLogo />
        </Flex>
      </Flex>

      <Flex
        flex={1}
        direction="column"
        align="center"
        justify="center"
        textAlign="center"
        p={4}
      >
        <Text textStyle={'headingSection'} mb={1}>
          Decline Review Invitation
        </Text>
        <Text textStyle={'textBody'} mb={6}>
          Are you sure you want to decline this review request?
        </Text>

        <Button
          variant="caution"
          size="lg"
          onClick={handleDeclineClick}
          isLoading={isProcessing}
          loadingText="Declining..."
        >
          Decline Review
        </Button>
      </Flex>
    </Flex>
  );
};
